import React, { useEffect, useState, useRef } from 'react';
import { SecondaryBase } from '../Commons/SecondaryBase';
import { 
    StartSequence, 
    ResetSequence, 
    GetState, 
    ShowFrontPage, 
    GetSequenceHistory, 
    ClearSequenceHistory, 
    RegisterTap,
    ProjectGame,
    GetSequenceThemes,
    GetSequenceTheme,
    SaveSequenceTheme,
    DeleteSequenceTheme,
    GetSequenceConfig,
    SaveSequenceConfig,
    UploadSequenceMedia
} from '../../../utilities/BL/Sequence';
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage';
import { PremiumQR } from './../../Commons/PremiumQR';
import { Confirm } from './../../Commons/Modals/Confirm';

const Sequence = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [screenId, setScreenId] = useState(1);
    const [sequenceLength, setSequenceLength] = useState(7);
    const [playbackSpeedMs, setPlaybackSpeedMs] = useState(500);

    const [gameState, setGameState] = useState({
        phase: 'WAITING',
        sequence: [],
        playerStep: 0,
        isPlaying: false,
        matchId: '',
        buttons: []
    });

    const [isWorking, setIsWorking] = useState(false);
    const [error, setError] = useState('');
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [history, setHistory] = useState([]);
    
    // Temas y Configuración
    const [themesList, setThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState('');
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({ message: '', action: null });

    // Estado del Formulario del Tema en Edición
    const defaultThemeForm = {
        id: 0,
        name: '',
        buttons: [
            { id: 0, color: '#00ff41', imageUrl: '', label: 'VERDE' },
            { id: 1, color: '#ff003c', imageUrl: '', label: 'ROJO' },
            { id: 2, color: '#fffb00', imageUrl: '', label: 'AMARILLO' },
            { id: 3, color: '#00e1ff', imageUrl: '', label: 'AZUL' },
            { id: 4, color: '#b300ff', imageUrl: '', label: 'MORADO' },
            { id: 5, color: '#ff8800', imageUrl: '', label: 'NARANJA' }
        ]
    };
    const [themeForm, setThemeForm] = useState(defaultThemeForm);
    const [isUploading, setIsUploading] = useState({});

    // Modales QR
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrTargetUrl, setQrTargetUrl] = useState('');
    const [tvTargetUrl, setTvTargetUrl] = useState('');
    const [qrTitle, setQrTitle] = useState('');
    
    const socketRef = useRef(null);

    // Cargar historial
    const loadHistory = async () => {
        const res = await GetSequenceHistory();
        if (res && res.success && res.data) {
            setHistory(res.data);
        }
    };

    // Cargar Temas
    const loadThemes = async () => {
        const res = await GetSequenceThemes();
        if (res && res.success && res.data) {
            setThemesList(res.data);
        }
    };

    // Cargar Configuración de Pantalla
    const loadScreenConfig = async (sId) => {
        const res = await GetSequenceConfig(sId);
        if (res && res.success && res.data) {
            setSequenceLength(res.data.sequenceLength);
            setPlaybackSpeedMs(res.data.playbackSpeedMs);
            setSelectedThemeId(res.data.themeId || '');
        } else {
            setSequenceLength(7);
            setPlaybackSpeedMs(500);
            setSelectedThemeId('');
        }
    };

    // Auto-guardado de Configuración
    const autoSaveConfig = async (sId, tId, len, speed) => {
        await SaveSequenceConfig({
            screenId: sId,
            themeId: tId ? parseInt(tId) : null,
            sequenceLength: len,
            playbackSpeedMs: speed
        });
    };

    // Conectar WebSocket de monitoreo
    useEffect(() => {
        const connectWS = () => {
            const wssUrl = `${process.env.REACT_APP_API_URL_WSS}?tipo=superadmin`;
            setConnectionStatus('connecting');
            
            const ws = new WebSocket(wssUrl);
            socketRef.current = ws;

            ws.onopen = () => {
                setConnectionStatus('connected');
                setError('');
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    if (message && message.type && message.type.toLowerCase().startsWith('sequence.')) {
                        const payload = message.payload;
                        if (payload && payload.screenId === screenId) {
                            setGameState(payload);
                            if (payload.phase === 'WON' || payload.phase === 'LOST') {
                                loadHistory();
                            }
                        }
                    }
                } catch (e) {
                    console.error('Error parseando mensaje WS:', e);
                }
            };

            ws.onclose = () => {
                setConnectionStatus('disconnected');
                setTimeout(connectWS, 3000);
            };

            ws.onerror = () => {
                setConnectionStatus('error');
            };
        };

        connectWS();
        fetchCurrentState();
        loadHistory();
        loadThemes();
        loadScreenConfig(screenId);

        return () => {
            if (socketRef.current) {
                socketRef.current.onclose = null;
                socketRef.current.close();
            }
        };
    }, [screenId]);

    const fetchCurrentState = async () => {
        const res = await GetState(screenId);
        if (res && res.success && res.data) {
            setGameState(res.data);
        }
    };

    const handleStart = async () => {
        if (!selectedThemeId) {
            showMessage('Por favor, selecciona un tema activo primero.', MESSAGE_TYPE.WARNING);
            return;
        }
        setIsWorking(true);
        setError('');
        showMessage('Iniciando juego...', MESSAGE_TYPE.INFO);
        
        // Buscamos si hay tema activo para enviar los botones correctos
        let buttonsToSend = null;
        const activeTheme = themesList.find(t => t.id === parseInt(selectedThemeId));
        if (activeTheme && activeTheme.buttonsJson) {
            try {
                buttonsToSend = JSON.parse(activeTheme.buttonsJson);
            } catch {}
        }

        const res = await StartSequence({
            screenId,
            sequenceLength,
            playbackSpeedMs,
            buttons: buttonsToSend
        });

        if (res.success) {
            showMessage('🚀 Secuencia iniciada', MESSAGE_TYPE.SUCCESS);
        } else {
            setError(res.message || 'Error al iniciar la dinámica.');
            showMessage('Error al iniciar', MESSAGE_TYPE.ERROR);
        }
        setIsWorking(false);
    };

    const handleReset = async () => {
        if (!selectedThemeId) {
            showMessage('Por favor, selecciona un tema activo primero.', MESSAGE_TYPE.WARNING);
            return;
        }
        setIsWorking(true);
        setError('');
        showMessage('Reiniciando...', MESSAGE_TYPE.INFO);
        const res = await ResetSequence(screenId);
        if (res.success) {
            showMessage('🔄 Juego reiniciado', MESSAGE_TYPE.SUCCESS);
            setGameState({
                phase: 'WAITING',
                sequence: [],
                playerStep: 0,
                isPlaying: false,
                matchId: '',
                buttons: []
            });
        } else {
            setError(res.message || 'Error al resetear la dinámica.');
            showMessage('Error al resetear', MESSAGE_TYPE.ERROR);
        }
        setIsWorking(false);
    };

    const handleProjectGameManual = async () => {
        if (!selectedThemeId) {
            showMessage('Por favor, selecciona un tema activo primero.', MESSAGE_TYPE.WARNING);
            return;
        }
        showMessage('Proyectando juego...', MESSAGE_TYPE.INFO);
        const res = await ProjectGame(screenId);
        if (res.success) {
            showMessage('📺 Tablero proyectado en la pantalla', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('Error al proyectar', MESSAGE_TYPE.ERROR);
        }
    };

    const handleShowFrontPageManual = async () => {
        showMessage('Mostrando portada...', MESSAGE_TYPE.INFO);
        const res = await ShowFrontPage(screenId);
        if (res.success) {
            showMessage('📺 Portada mostrada', MESSAGE_TYPE.SUCCESS);
        } else {
            showMessage('Error al mostrar portada', MESSAGE_TYPE.ERROR);
        }
    };

    const openQRModal = () => {
        const tvUrl = `${window.location.origin}/screen`;
        setTvTargetUrl(tvUrl);

        const playerUrl = `${window.location.origin}/sequence?screenId=${screenId}`;
        setQrTargetUrl(playerUrl);

        setQrTitle(`Secuencia - Pantalla ${screenId}`);
        setShowQRModal(true);
    };

    // Toque de emergencia desde el admin
    const handleEmergencyTap = async (colorId) => {
        if (!gameState.isPlaying || gameState.phase !== 'PLAYER_INPUT') {
            showMessage('No es el turno del jugador', MESSAGE_TYPE.WARNING);
            return;
        }
        showMessage(`Toque manual enviado (Botón ${colorId})`, MESSAGE_TYPE.INFO);
        const res = await RegisterTap(screenId, gameState.matchId, colorId);
        if (!res.success) {
            showMessage('Error al registrar toque', MESSAGE_TYPE.ERROR);
        }
    };

    const handleClearHistory = async () => {
        setConfirmConfig({
            message: '¿Estás seguro de eliminar todo el historial de secuencias?',
            action: async () => {
                const res = await ClearSequenceHistory();
                if (res && res.success) {
                    showMessage('Historial limpio', MESSAGE_TYPE.SUCCESS);
                    loadHistory();
                } else {
                    showMessage('Error al limpiar historial', MESSAGE_TYPE.ERROR);
                }
            }
        });
        setShowConfirmModal(true);
    };

    // ==========================================
    // GESTIÓN DE TEMAS (MODAL)
    // ==========================================
    const openCreateTheme = () => {
        setThemeForm(defaultThemeForm);
        setShowThemeModal(true);
    };

    const openEditTheme = (theme) => {
        try {
            const btns = JSON.parse(theme.buttonsJson);
            setThemeForm({
                id: theme.id,
                name: theme.name,
                buttons: btns
            });
        } catch {
            setThemeForm({
                id: theme.id,
                name: theme.name,
                buttons: defaultThemeForm.buttons
            });
        }
        setShowThemeModal(true);
    };

    const handleDeleteThemeClick = (themeId) => {
        setConfirmConfig({
            message: '¿Estás seguro de eliminar este tema permanentemente?',
            action: async () => {
                const res = await DeleteSequenceTheme(themeId);
                if (res && res.success) {
                    showMessage('Tema eliminado', MESSAGE_TYPE.SUCCESS);
                    loadThemes();
                    if (parseInt(selectedThemeId) === themeId) {
                        setSelectedThemeId('');
                        autoSaveConfig(screenId, '', sequenceLength, playbackSpeedMs);
                    }
                } else {
                    showMessage('Error al eliminar', MESSAGE_TYPE.ERROR);
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleSaveTheme = async () => {
        if (!themeForm.name.trim()) {
            showMessage('Por favor ingresa un nombre para el tema.', MESSAGE_TYPE.WARNING);
            return;
        }

        const payload = {
            id: themeForm.id,
            name: themeForm.name.trim(),
            buttonsJson: JSON.stringify(themeForm.buttons)
        };

        showMessage('Guardando tema...', MESSAGE_TYPE.INFO);
        const res = await SaveSequenceTheme(payload);
        if (res && res.success) {
            showMessage('✅ Tema guardado exitosamente', MESSAGE_TYPE.SUCCESS);
            setShowThemeModal(false);
            loadThemes();
        } else {
            showMessage('Error al guardar tema', MESSAGE_TYPE.ERROR);
        }
    };

    const handleUploadMediaForButton = async (file, btnId) => {
        setIsUploading(prev => ({ ...prev, [btnId]: true }));
        showMessage('Subiendo imagen...', MESSAGE_TYPE.INFO);
        
        const res = await UploadSequenceMedia(file);
        if (res && res.success && res.url) {
            showMessage('✅ Imagen subida', MESSAGE_TYPE.SUCCESS);
            setThemeForm(prev => ({
                ...prev,
                buttons: prev.buttons.map(b => b.id === btnId ? { ...b, imageUrl: res.url } : b)
            }));
        } else {
            showMessage('Error al subir imagen', MESSAGE_TYPE.ERROR);
        }
        setIsUploading(prev => ({ ...prev, [btnId]: false }));
    };

    const updateThemeButtonField = (btnId, field, value) => {
        setThemeForm(prev => ({
            ...prev,
            buttons: prev.buttons.map(b => b.id === btnId ? { ...b, [field]: value } : b)
        }));
    };

    // Cambiar configuración del slider de longitud o velocidad
    const handleLengthChange = (val) => {
        setSequenceLength(val);
        autoSaveConfig(screenId, selectedThemeId, val, playbackSpeedMs);
    };

    const handleSpeedChange = (val) => {
        setPlaybackSpeedMs(val);
        autoSaveConfig(screenId, selectedThemeId, sequenceLength, val);
    };

    const handleThemeChange = (val) => {
        setSelectedThemeId(val);
        autoSaveConfig(screenId, val, sequenceLength, playbackSpeedMs);
    };

    const getMediaUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http://') || path.startsWith('https://')) return path;
        const apiBase = (process.env.REACT_APP_API_URL || '').replace('/api/', '/').replace('/api', '/');
        return `${apiBase}${path}`;
    };

    const activeButtons = gameState.buttons && gameState.buttons.length > 0 
        ? gameState.buttons 
        : customButtonsFromActiveTheme();

    function customButtonsFromActiveTheme() {
        if (selectedThemeId) {
            const activeTheme = themesList.find(t => t.id === parseInt(selectedThemeId));
            if (activeTheme && activeTheme.buttonsJson) {
                try {
                    return JSON.parse(activeTheme.buttonsJson);
                } catch {}
            }
        }
        return defaultThemeForm.buttons;
    }

    return (
        <SecondaryBase getOutOfTheDynamic={getOutOfTheDynamic}>
            {showConfirmModal && (
                <Confirm 
                    message={confirmConfig.message} 
                    actionToYesButton={confirmConfig.action} 
                    textYesButton="Sí, Confirmar" 
                    textNoButton="Cancelar" 
                    setShowModal={setShowConfirmModal} 
                />
            )}

            {showQRModal && (
                <>
                    <div className="cModal__Background" onClick={() => setShowQRModal(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999 }}>
                        <div className="cModal__Container__Content position-relative p-0" style={{ background: 'transparent', boxShadow: 'none' }}>
                            <button type="button" className="btn-close position-absolute top-0 end-0 m-3 bg-dark p-2 rounded-circle" onClick={() => setShowQRModal(false)} style={{ zIndex: 20 }}></button>
                            <div className="bg-dark text-white p-3 rounded-top-4 text-center border-bottom border-secondary">
                                <span className="d-block small text-warning mb-1"><i className="bi bi-tv"></i> Abre este link en tu TV o pantalla de proyección:</span>
                                <span className="fw-bold user-select-all text-break">{tvTargetUrl}</span>
                            </div>
                            <PremiumQR url={qrTargetUrl} title={qrTitle} />
                        </div>
                    </div>
                </>
            )}

            {/* MODAL DE EDICIÓN DE TEMA */}
            {showThemeModal && (
                <>
                    <div className="cModal__Background" style={{ zIndex: 99998 }} onClick={() => setShowThemeModal(false)}></div>
                    <div className="cModal__Container" style={{ zIndex: 99999, maxWidth: '800px', width: '90vw' }}>
                        <div className="cModal__Container__Content bg-white text-dark p-4 rounded-4 shadow-lg">
                            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                                <h3 className="fw-bold text-primary mb-0">
                                    {themeForm.id === 0 ? '✨ Crear Nuevo Tema' : '✏️ Editar Tema'}
                                </h3>
                                <button type="button" className="btn-close" onClick={() => setShowThemeModal(false)}></button>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-bold text-muted">Nombre del Tema / Patrocinador</label>
                                <input 
                                    type="text" 
                                    className="form-control form-control-lg fw-bold" 
                                    placeholder="Ej: Tecate, Coca-Cola, Navideño..."
                                    value={themeForm.name}
                                    onChange={(e) => setThemeForm(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>

                            <h5 className="fw-bold text-muted mb-3 border-bottom pb-1">Configuración de los 6 Cuadros</h5>
                            <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '5px' }}>
                                <div className="row g-3">
                                    {themeForm.buttons.map((btn) => (
                                        <div key={btn.id} className="col-md-6">
                                            <div className="p-3 border rounded bg-light">
                                                <div className="d-flex align-items-center gap-2 mb-2">
                                                    <span className="badge bg-primary rounded-circle">{btn.id}</span>
                                                    <input 
                                                        type="text" 
                                                        className="form-control form-control-sm fw-bold text-uppercase" 
                                                        placeholder="Etiqueta" 
                                                        value={btn.label || ''} 
                                                        onChange={(e) => updateThemeButtonField(btn.id, 'label', e.target.value)}
                                                    />
                                                </div>

                                                <div className="mb-2">
                                                    <label className="small text-muted fw-bold d-block mb-1">Color de Fondo</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input 
                                                            type="color" 
                                                            className="form-control form-control-color" 
                                                            value={btn.color.startsWith('#') ? btn.color : '#ffffff'} 
                                                            onChange={(e) => updateThemeButtonField(btn.id, 'color', e.target.value)}
                                                            style={{ width: '38px', height: '38px', padding: '0', border: '0', borderRadius: '4px' }}
                                                        />
                                                        <input 
                                                            type="text" 
                                                            className="form-control form-control-sm" 
                                                            placeholder="#Hex"
                                                            value={btn.color}
                                                            onChange={(e) => updateThemeButtonField(btn.id, 'color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="small text-muted fw-bold d-block mb-1">Imagen del Cuadro</label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-outline-dark btn-sm text-nowrap fw-bold"
                                                            onClick={() => document.getElementById(`media-upload-btn-${btn.id}`).click()}
                                                            disabled={isUploading[btn.id]}
                                                        >
                                                            {isUploading[btn.id] ? 'Subiendo...' : <><i className="bi bi-upload"></i> Subir Imagen</>}
                                                        </button>
                                                        <input 
                                                            type="file" 
                                                            id={`media-upload-btn-${btn.id}`} 
                                                            className="d-none" 
                                                            accept="image/*"
                                                            onChange={(e) => {
                                                                const file = e.target.files[0];
                                                                if (file) handleUploadMediaForButton(file, btn.id);
                                                            }}
                                                        />
                                                        {btn.imageUrl && (
                                                            <div className="border rounded p-1 bg-white" style={{ position: 'relative' }}>
                                                                <img src={getMediaUrl(btn.imageUrl)} alt="Btn" style={{ width: '34px', height: '34px', objectFit: 'contain' }} />
                                                                <button 
                                                                    type="button" 
                                                                    className="btn-close position-absolute top-0 end-0 bg-danger rounded-circle p-1" 
                                                                    style={{ fontSize: '6px', transform: 'translate(50%, -50%)' }}
                                                                    onClick={() => updateThemeButtonField(btn.id, 'imageUrl', '')}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                                <button className="btn btn-outline-secondary fw-bold px-4" onClick={() => setShowThemeModal(false)}>Cancelar</button>
                                <button className="btn btn-primary fw-bold px-4" onClick={handleSaveTheme}>Guardar Tema</button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="container py-4 text-dark" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                    <div>
                        <h2 className="fw-black text-uppercase text-primary mb-0">
                            🎮 CONFIGURACIÓN DE SECUENCIAS
                        </h2>
                        <p className="text-muted small mb-0">
                            Crea temas con colores e imágenes, proyecta el juego y monitorea en tiempo real.
                        </p>
                    </div>
                    <span className={`badge rounded-pill px-3 py-2 ${
                        connectionStatus === 'connected' ? 'bg-success' : 'bg-danger'
                    }`}>
                        {connectionStatus === 'connected' ? '● En línea (WS)' : '○ Desconectado'}
                    </span>
                </div>

                {error && (
                    <div className="alert alert-danger border-0 shadow-sm mb-4" role="alert">
                        ⚠️ {error}
                    </div>
                )}

                <div className="row g-4">
                    {/* Panel de Configuración y Parámetros */}
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm mb-4 rounded-4">
                            <div className="card-body p-4">
                                <h4 className="fw-bold mb-4 text-primary"><i className="bi bi-sliders"></i> PARÁMETROS</h4>
                                
                                {/* Screen Selector */}
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold">ID de Pantalla (ScreenId)</label>
                                    <select 
                                        className="form-select bg-light border-1 p-3 fw-bold text-dark"
                                        value={screenId}
                                        onChange={(e) => setScreenId(parseInt(e.target.value))}
                                        disabled={gameState.isPlaying && gameState.phase !== 'WAITING'}
                                        style={{ borderRadius: '12px' }}
                                    >
                                        <option value={1}>Pantalla 1 (Estadio Principal)</option>
                                        <option value={2}>Pantalla 2 (Stand Lateral)</option>
                                        <option value={3}>Pantalla 3 (Stand Entrada)</option>
                                        <option value={4}>Pantalla 4 (Stand VIP)</option>
                                    </select>
                                </div>

                                {/* Selección de Tema */}
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold d-flex justify-content-between">
                                        <span>Seleccionar Tema Activo</span>
                                        <button className="btn btn-link btn-sm p-0 text-decoration-none fw-bold" onClick={openCreateTheme}>+ Crear Nuevo Tema</button>
                                    </label>
                                    <div className="d-flex gap-2">
                                        <select 
                                            className="form-select bg-light border-1 p-3 fw-bold text-primary flex-grow-1"
                                            value={selectedThemeId}
                                            onChange={(e) => handleThemeChange(e.target.value)}
                                            style={{ borderRadius: '12px' }}
                                        >
                                            <option value="">-- Tema por Defecto --</option>
                                            {themesList.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                        {selectedThemeId && (
                                            <button 
                                                className="btn btn-outline-secondary px-3" 
                                                onClick={() => openEditTheme(themesList.find(t => t.id === parseInt(selectedThemeId)))}
                                                style={{ borderRadius: '12px' }}
                                                title="Editar este tema"
                                            >
                                                <i className="bi bi-pencil-fill"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Sequence Length */}
                                <div className="mb-3">
                                    <label className="form-label text-muted fw-bold d-flex justify-content-between">
                                        <span>Longitud de la Secuencia</span>
                                        <span className="text-primary fw-black">{sequenceLength} pasos</span>
                                    </label>
                                    <input 
                                        type="range" 
                                        className="form-range" 
                                        min="3" 
                                        max="12" 
                                        value={sequenceLength}
                                        onChange={(e) => handleLengthChange(parseInt(e.target.value))}
                                        disabled={gameState.isPlaying && gameState.phase !== 'WAITING'}
                                    />
                                    <div className="d-flex justify-content-between text-muted small mt-1">
                                        <span>Fácil (3)</span>
                                        <span>Difícil (12)</span>
                                    </div>
                                </div>

                                {/* Playback Speed */}
                                <div className="mb-4">
                                    <label className="form-label text-muted fw-bold d-flex justify-content-between">
                                        <span>Velocidad de Destello</span>
                                        <span className="text-primary fw-black">{playbackSpeedMs} ms</span>
                                    </label>
                                    <input 
                                        type="range" 
                                        className="form-range" 
                                        min="300" 
                                        max="1200" 
                                        step="50"
                                        value={playbackSpeedMs}
                                        onChange={(e) => handleSpeedChange(parseInt(e.target.value))}
                                        disabled={gameState.isPlaying && gameState.phase !== 'WAITING'}
                                    />
                                    <div className="d-flex justify-content-between text-muted small mt-1">
                                        <span>Rápido (300ms)</span>
                                        <span>Lento (1200ms)</span>
                                    </div>
                                </div>

                                {/* Botones de Control Principal */}
                                <div className="d-grid gap-3 pt-2">
                                    <button 
                                        className="btn btn-success btn-lg fw-bold p-3 text-uppercase text-white shadow-sm"
                                        onClick={handleProjectGameManual}
                                        style={{ borderRadius: '12px', letterSpacing: '1px' }}
                                    >
                                        📺 Proyectar Juego
                                    </button>
                                    
                                    <div className="row g-2">
                                        <div className="col-6">
                                            <button 
                                                className="btn btn-primary btn-md fw-bold w-100 p-3 text-uppercase"
                                                onClick={handleStart}
                                                disabled={isWorking || (gameState.isPlaying && gameState.phase !== 'WAITING')}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                🚀 Iniciar Juego
                                            </button>
                                        </div>
                                        <div className="col-6">
                                            <button 
                                                className="btn btn-outline-danger btn-md fw-bold w-100 p-3 text-uppercase"
                                                onClick={handleReset}
                                                disabled={isWorking}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                🔄 Resetear
                                            </button>
                                        </div>
                                    </div>

                                    <div className="row g-2">
                                        <div className="col-6">
                                            <button 
                                                className="btn btn-outline-secondary btn-md fw-bold w-100 p-3 text-uppercase"
                                                onClick={handleShowFrontPageManual}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                🖼️ Mostrar Portada
                                            </button>
                                        </div>
                                        <div className="col-6">
                                            <button 
                                                className="btn btn-outline-primary btn-md fw-bold w-100 p-3 text-uppercase"
                                                onClick={openQRModal}
                                                style={{ borderRadius: '12px' }}
                                            >
                                                📲 Instrucciones
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Listado de Temas Creados */}
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="fw-bold mb-0 text-primary"><i className="bi bi-journal-album"></i> TEMAS DISPONIBLES</h4>
                                    <button className="btn btn-primary btn-sm fw-bold px-3" onClick={openCreateTheme} style={{ borderRadius: '8px' }}>
                                        <i className="bi bi-plus-circle-fill"></i> Crear Tema
                                    </button>
                                </div>
                                {themesList.length === 0 ? (
                                    <div className="text-center py-3 text-muted small">No hay temas personalizados. Crea uno.</div>
                                ) : (
                                    <div className="list-group">
                                        {themesList.map(t => (
                                            <div key={t.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center rounded-3 mb-2 border border-light shadow-sm">
                                                <div>
                                                    <span className="fw-bold text-dark">{t.name}</span>
                                                    <span className="text-muted small d-block">ID: {t.id} • Creado el {new Date(t.createdAt).toLocaleDateString()}</span>
                                                </div>
                                                <div className="btn-group">
                                                    <button className="btn btn-outline-primary btn-sm fw-bold" onClick={() => openEditTheme(t)}><i className="bi bi-pencil-square"></i></button>
                                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteThemeClick(t.id)}><i className="bi bi-trash-fill"></i></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Monitoreo en Vivo */}
                    <div className="col-lg-6">
                        <div className="card border-0 shadow-sm mb-4 rounded-4">
                            <div className="card-body p-4">
                                <h4 className="fw-bold mb-4 text-primary"><i className="bi bi-display"></i> MONITOREO EN VIVO</h4>
                                
                                {/* Estado Actual */}
                                <div className="mb-4 text-center">
                                    <span className={`fs-5 fw-bold px-4 py-2 rounded-3 text-uppercase d-inline-block shadow-sm ${
                                        gameState.phase === 'WAITING' ? 'bg-secondary text-white' :
                                        gameState.phase === 'PLAYBACK' ? 'bg-warning text-dark animate-pulse' :
                                        gameState.phase === 'PLAYER_INPUT' ? 'bg-primary text-white' :
                                        gameState.phase === 'WON' ? 'bg-success text-white' :
                                        'bg-danger text-white'
                                    }`}>
                                        {gameState.phase === 'WAITING' && '⏳ Esperando inicio'}
                                        {gameState.phase === 'PLAYBACK' && '📺 Reproduciendo secuencia'}
                                        {gameState.phase === 'PLAYER_INPUT' && '📱 Turno del aficionado'}
                                        {gameState.phase === 'WON' && '🏆 ¡SECUENCIA CORRECTA!'}
                                        {gameState.phase === 'LOST' && '❌ ¡SECUENCIA FALLIDA!'}
                                    </span>
                                </div>

                                {/* Secuencia Generada */}
                                {gameState.sequence && gameState.sequence.length > 0 && (
                                    <div className="mb-4">
                                        <label className="text-muted fw-bold d-block mb-2">Secuencia del Juego</label>
                                        <div className="bg-light p-3 rounded-3 border d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
                                            {gameState.sequence.map((colorId, i) => {
                                                const btnConfig = activeButtons.find(b => b.id === colorId) || { color: '#888', label: '?' };
                                                const isCurrent = gameState.phase === 'PLAYER_INPUT' && i === gameState.playerStep;
                                                return (
                                                    <div key={i} className="d-flex align-items-center">
                                                        <span 
                                                            className="rounded-circle d-inline-block border"
                                                            style={{ 
                                                                width: '24px', 
                                                                height: '24px', 
                                                                backgroundColor: btnConfig.color,
                                                                transform: isCurrent ? 'scale(1.2)' : 'none',
                                                                boxShadow: isCurrent ? '0 0 10px rgba(0,0,0,0.5)' : 'none'
                                                            }}
                                                            title={btnConfig.label}
                                                        />
                                                        {i < gameState.sequence.length - 1 && (
                                                            <span className="text-muted mx-1">→</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Progreso */}
                                {gameState.phase === 'PLAYER_INPUT' && (
                                    <div className="mb-4">
                                        <label className="text-muted fw-bold d-block mb-1">Progreso del Jugador</label>
                                        <div className="progress bg-light border" style={{ height: '24px', borderRadius: '12px' }}>
                                            <div 
                                                className="progress-bar progress-bar-striped progress-bar-animated bg-info" 
                                                role="progressbar" 
                                                style={{ width: `${(gameState.playerStep / gameState.sequence.length) * 100}%` }}
                                            >
                                                <span className="fw-black text-dark fs-6">{gameState.playerStep} / {gameState.sequence.length}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Controles de Emergencia */}
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <h4 className="fw-bold mb-2 text-danger"><i className="bi bi-cone-striped"></i> CONTROLES DE EMERGENCIA</h4>
                                <p className="text-muted small mb-4">
                                    Haz clic en estos botones para ingresar toques manualmente si falla el celular del aficionado.
                                </p>
                                
                                <div className="row g-3">
                                    {activeButtons.map((btn) => (
                                        <div key={btn.id} className="col-4">
                                            <button
                                                className="btn w-100 py-3 border-2 shadow-sm fw-bold d-flex flex-column align-items-center justify-content-center"
                                                onClick={() => handleEmergencyTap(btn.id)}
                                                disabled={gameState.phase !== 'PLAYER_INPUT'}
                                                style={{
                                                    borderColor: btn.color,
                                                    background: 'white',
                                                    color: 'black',
                                                    borderRadius: '16px',
                                                    opacity: gameState.phase !== 'PLAYER_INPUT' ? 0.6 : 1
                                                }}
                                            >
                                                {btn.imageUrl ? (
                                                    <img src={getMediaUrl(btn.imageUrl)} alt={btn.label} style={{ width: '32px', height: '32px', objectFit: 'contain', marginBottom: '5px' }} />
                                                ) : (
                                                    <span className="rounded-circle d-inline-block mb-2 border" style={{ width: '20px', height: '20px', backgroundColor: btn.color }} />
                                                )}
                                                <span className="small text-uppercase text-truncate w-100">{btn.label || `Palo ${btn.id}`}</span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Historial de Partidas */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card border-0 shadow-sm rounded-4">
                            <div className="card-body p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h4 className="fw-bold mb-0 text-primary"><i className="bi bi-clock-history"></i> HISTORIAL DE PARTIDAS</h4>
                                    {history.length > 0 && (
                                        <button className="btn btn-outline-danger btn-sm fw-bold" onClick={handleClearHistory}>
                                            <i className="bi bi-trash"></i> Limpiar Historial
                                        </button>
                                    )}
                                </div>

                                {history.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        No hay registros de partidas jugadas aún.
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Partida ID</th>
                                                    <th>Pantalla</th>
                                                    <th>Pasos Totales</th>
                                                    <th>Pasos Logrados</th>
                                                    <th>Resultado</th>
                                                    <th>Fecha</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {history.map((h) => (
                                                    <tr key={h.id}>
                                                        <td><code className="text-secondary">{h.matchId}</code></td>
                                                        <td>Pantalla {h.screenId}</td>
                                                        <td>{h.sequenceLength}</td>
                                                        <td>{h.playerStep}</td>
                                                        <td>
                                                            <span className={`badge ${h.hasWon ? 'bg-success' : 'bg-danger'} px-3 py-2`}>
                                                                {h.hasWon ? 'GANÓ' : 'PERDIÓ'}
                                                            </span>
                                                        </td>
                                                        <td>{new Date(h.playedAt).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </SecondaryBase>
    );
};

export { Sequence };
